// Formatting for the admin Video Quality tab: plain-English metric names and how a
// value or band reads with its unit. Kept apart from the page so the page exports only
// components (react-refresh) and these stay unit-testable on their own.

import type { QualityMetric } from '@/features/admin/services/qualityService';

// Plain-English names for the rubric's metric ids. An id missing here is shown as-is,
// so a metric the pipeline adds later still renders.
const METRIC_LABELS: Record<string, string> = {
  words_per_6s: 'Words per 6s',
  overrun_scene_pct: 'Scenes over the word limit',
  dead_air_scene_pct: 'Near-silent scenes',
  narration_recall: 'Source text narrated',
  narration_precision: 'Narration from the source',
  spoken_placeholders: 'Markup in narration',
  speech_rate_wps: 'Speaking rate',
  rushed_scene_pct: 'Rushed scenes',
  silent_scene_pct: 'Silent scenes',
  sped_up_scene_pct: 'Sped-up scenes',
  longest_dead_air_s: 'Longest silence',
  speech_coverage: 'Narration coverage',
  has_audio: 'Audio track',
  aspect_ratio_ok: 'Aspect ratio',
  duration_overrun_ratio: 'Length vs plan',
  longest_freeze_s: 'Longest freeze',
  frozen_pct: 'Frozen picture',
  motion_reversal_scene_pct: 'Motion reversals',
  picture_ends_early_s: 'Picture ends early',
  black_seconds: 'Black frames',
  loudness_lufs: 'Loudness',
  true_peak_dbtp: 'Peak level',
};

export function metricLabel(name: string): string {
  return METRIC_LABELS[name] ?? name;
}

function formatNumber(value: number, unit: string): string {
  if (unit === 'bool') return value >= 1 ? 'yes' : 'no';
  const digits = unit === 'ratio' || unit === 'words/s' ? 2 : 1;
  const n = Number.isInteger(value) ? String(value) : value.toFixed(digits);
  if (unit === '%') return `${n}%`;
  return unit && unit !== 'ratio' ? `${n} ${unit}` : n;
}

export function formatValue(metric: Pick<QualityMetric, 'value' | 'unit'>): string {
  return metric.value === null ? '—' : formatNumber(metric.value, metric.unit);
}

export function formatBand(band: (number | null)[], unit: string): string {
  const [lo, hi] = band;
  if (unit === 'bool') return 'yes';
  if (lo !== null && lo !== undefined && hi !== null && hi !== undefined) {
    return `${formatNumber(lo, '')} to ${formatNumber(hi, unit)}`;
  }
  if (hi !== null && hi !== undefined) return `≤ ${formatNumber(hi, unit)}`;
  if (lo !== null && lo !== undefined) return `≥ ${formatNumber(lo, unit)}`;
  return '—';
}

// Reasons arrive as "metric_id=12.34 (fail)"; shown as "Loudness (fail)".
export function describeReason(reason: string): string {
  const match = /^([a-z0-9_]+)=.*\((\w+)\)$/.exec(reason);
  return match ? `${metricLabel(match[1])} (${match[2]})` : reason;
}
